export class VideoStreamController {
  private mediaSource: MediaSource;
  private sourceBuffer!: SourceBuffer;
  private queue: Array<{ chunk: Uint8Array; number: number }> = [];
  private isReady = false;
  private hasInitSegment = false;
  private videoEl: HTMLVideoElement;
  private sourceOpenHandled = false;
  private isRemoving = false;
  private readonly MAX_QUEUE_SIZE = 50;
  private chunkCount = 0;
  private playAttempted = false;
  private shouldEndWhenReady = false;
  private pendingEndStream = false;
  private onStreamEndedCallback?: () => void;
  private isDestroyed = false;

  // MP4 box buffering for proper chunk reassembly
  private byteBuffer = new Uint8Array(0);
  private pendingMoof: Uint8Array | null = null;
  private initBoxes: Uint8Array[] = [];

  /**
   * Force video end: dispatch synthetic 'ended' event so the UI's
   * onEnded handler runs through the normal single path.
   */
  private forceVideoEnd() {
    console.log('🎬 Forcing video end with synthetic event');

    // Dispatch synthetic 'ended' event so ProfileSection's onEnded handler fires
    const endedEvent = new Event('ended', { bubbles: false, cancelable: false });
    this.videoEl.dispatchEvent(endedEvent);
  }

  constructor(
    videoEl: HTMLVideoElement,
    private mimeCodec = 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"',
    primePlayback = false
  ) {
    this.videoEl = videoEl;
    
    // iOS Safari 17+ uses ManagedMediaSource, older browsers use MediaSource
    const MediaSourceConstructor = 
      (typeof window !== 'undefined' && (window as typeof window & { ManagedMediaSource?: typeof MediaSource }).ManagedMediaSource) ||
      (typeof window !== 'undefined' && window.MediaSource) ||
      (typeof globalThis !== 'undefined' && (globalThis as typeof window).MediaSource) ||
      (typeof self !== 'undefined' && (self as typeof window).MediaSource);
    
    if (!MediaSourceConstructor) {
      console.error('❌ MediaSource API not available');
      throw new Error('MediaSource API not available');
    }
    
    if (!MediaSourceConstructor.isTypeSupported(this.mimeCodec)) {
      console.error('❌ Codec not supported:', this.mimeCodec);
      throw new Error(`Codec not supported: ${this.mimeCodec}`);
    }
    
    console.log('🎬 Using MediaSource:', MediaSourceConstructor.name);
    this.mediaSource = new MediaSourceConstructor();
    videoEl.src = URL.createObjectURL(this.mediaSource);

    // iOS: Prime playback immediately in user gesture context
    if (primePlayback) {
      console.log('🎯 Priming video playback for iOS');
      this.playAttempted = true;
      videoEl.play().catch((e) => {
        console.log('⚠️ Initial play failed (expected):', e.message);
        // Reset flag so we can try again when data is ready
        this.playAttempted = false;
      });
    }

    this.mediaSource.addEventListener('sourceopen', this.onSourceOpen);

    videoEl.addEventListener('error', () => {
      if (videoEl.error) {
        console.error(
          '❌ Video error code:',
          videoEl.error.code,
          'message:',
          videoEl.error.message
        );
      }
    });

    videoEl.addEventListener('waiting', () => {
      console.log('⏸️ Video waiting for data, queue:', this.queue.length);
      // If we're waiting and MediaSource is ended (stream complete), force end
      if (this.mediaSource.readyState === 'ended' && this.queue.length === 0) {
        console.log('⚠️ Video waiting but stream ended - forcing video end');
        setTimeout(() => {
          if (this.videoEl.readyState < 3 && !this.videoEl.ended) {
            this.forceVideoEnd();
          }
        }, 500);
      }
    });

    videoEl.addEventListener('ended', () => {
      console.log('🎬 Video element ended event fired!');
    });

    videoEl.addEventListener('play', () => {
      console.log('▶️ Video element play event fired');
    });

    // Ensure playback starts when video is ready - backup trigger
    videoEl.addEventListener('canplay', () => {
      console.log('📺 Video canplay event, playAttempted:', this.playAttempted, 'paused:', videoEl.paused);
    });

    videoEl.addEventListener('pause', () => {
      console.log('⏸️ Video element pause event fired');
    });

    // Monitor video progress to detect when it reaches the end
    videoEl.addEventListener('timeupdate', () => {
      if (
        this.mediaSource.readyState === 'ended' &&
        videoEl.duration > 0 &&
        !Number.isNaN(videoEl.duration)
      ) {
        const timeRemaining = videoEl.duration - videoEl.currentTime;
        if (timeRemaining < 0.5 && !videoEl.ended) {
          console.log(
            '🔔 Video near end but ended event not fired, duration:',
            videoEl.duration,
            'currentTime:',
            videoEl.currentTime,
            'timeRemaining:',
            timeRemaining
          );
          // Manually seek to end to trigger ended event
          if (timeRemaining < 0.1) {
            console.log('📍 Manually seeking to end');
            videoEl.currentTime = videoEl.duration;
          }
        }
      }
    });
  }

  private onSourceOpen = () => {
    if (this.sourceOpenHandled) {
      console.warn('⚠️ sourceopen already handled');
      return;
    }
    this.sourceOpenHandled = true;

    const MediaSourceConstructor = 
      (typeof window !== 'undefined' && (window as typeof window & { ManagedMediaSource?: typeof MediaSource }).ManagedMediaSource) ||
      (typeof window !== 'undefined' && window.MediaSource) ||
      (typeof globalThis !== 'undefined' && (globalThis as typeof window).MediaSource) ||
      (typeof self !== 'undefined' && (self as typeof window).MediaSource);
      
    if (!MediaSourceConstructor || !MediaSourceConstructor.isTypeSupported(this.mimeCodec)) {
      console.error('❌ Unsupported codec:', this.mimeCodec);
      return;
    }

    this.sourceBuffer = this.mediaSource.addSourceBuffer(this.mimeCodec);
    this.sourceBuffer.mode = 'sequence';

    console.log('📊 SourceBuffer created with codec:', this.mimeCodec);
    console.log('📊 SourceBuffer mode:', this.sourceBuffer.mode);
    console.log('📊 SourceBuffer timestampOffset:', this.sourceBuffer.timestampOffset);

    this.sourceBuffer.addEventListener('updateend', () => {
      if (this.isRemoving) {
        this.isRemoving = false;
        console.log('🧹 Buffer removal complete');
      }

      // If endStream was called while updating, do it now
      if (this.pendingEndStream && this.mediaSource.readyState === 'open') {
        try {
          this.mediaSource.endOfStream();
          console.log('✅ MediaSource ended (deferred)');

          setTimeout(() => {
            if (!this.videoEl.ended && this.videoEl.duration > 0 && !Number.isNaN(this.videoEl.duration)) {
              const seekTo = Math.max(0, this.videoEl.duration - 0.1);
              this.videoEl.currentTime = seekTo;

              const onEnded = () => {
                this.videoEl.removeEventListener('ended', onEnded);
                if (this.onStreamEndedCallback) {
                  this.onStreamEndedCallback();
                }
              };

              this.videoEl.addEventListener('ended', onEnded);

              setTimeout(() => {
                this.videoEl.removeEventListener('ended', onEnded);
                if (this.onStreamEndedCallback && !this.videoEl.ended) {
                  this.onStreamEndedCallback();
                }
              }, 2000);
            } else if (this.onStreamEndedCallback) {
              // Route through synthetic ended event so UI/state update once.
              this.forceVideoEnd();
            }
          }, 100);

          this.pendingEndStream = false;
        } catch (e) {
          console.warn('⚠️ Failed to end MediaSource:', e);
        }
      }

      // Log buffer state
      if (this.sourceBuffer.buffered.length > 0) {
        const start = this.sourceBuffer.buffered.start(0);
        const end = this.sourceBuffer.buffered.end(this.sourceBuffer.buffered.length - 1);
        console.log(
          `📊 Buffered: ${start.toFixed(2)}s - ${end.toFixed(2)}s (${(end - start).toFixed(2)}s total)`
        );
      }

      // Single gated play trigger - only if not already attempted during construction
      if (
        !this.playAttempted &&
        this.videoEl.readyState >= 2 &&
        !this.videoEl.ended
      ) {
        this.playAttempted = true;
        console.log('▶️ Starting video playback, readyState:', this.videoEl.readyState);
        this.videoEl
          .play()
          .then(() => console.log('✅ video.play() resolved'))
          .catch((e) => {
            console.log('❌ Video play blocked:', e.message);
          });
      }

      // Process queue
      if (this.queue.length && !this.sourceBuffer.updating) {
        const next = this.queue.shift()!;
        console.log(
          `📦 Processing queued chunk #${next.number} from queue, size: ${next.chunk.length}`
        );
        try {
          this.sourceBuffer.appendBuffer(next.chunk);
          return;
        } catch (e) {
          console.error(
            `❌ SourceBuffer append failed for queued chunk #${next.number}:`,
            (e as Error).message
          );
          this.logBufferDiagnostics();
        }
      }

      // Then clean up old buffer when idle
      if (this.sourceBuffer.buffered.length > 0 && !this.isRemoving) {
        const currentTime = this.videoEl.currentTime;
        if (currentTime > 30 && this.sourceBuffer.buffered.start(0) < currentTime - 30) {
          try {
            this.isRemoving = true;
            this.sourceBuffer.remove(0, currentTime - 30);
          } catch {
            this.isRemoving = false;
          }
        }
      }
    });

    this.sourceBuffer.addEventListener('error', (err) => {
      console.error('🔴 SourceBuffer error event:', err);
      console.error(`❌ SourceBuffer error - bad chunk detected at position #${this.chunkCount}`);
      this.logBufferDiagnostics();

      // If MediaSource is ended due to error, we need to recreate it
      if (this.mediaSource.readyState === 'ended') {
        console.warn('⚠️ MediaSource ended due to error, recreating...');
        this.queue = [];
        this.recreateMediaSource();
      } else {
        console.warn('⚠️ Attempting to recover by clearing queue and continuing...');
        this.queue = [];
      }
    });

    this.isReady = true;
    console.log(
      '🟢 MediaSource is ready, initBoxes pending:',
      this.initBoxes.length,
      'queue pending:',
      this.queue.length
    );

    // After MediaSource recreation, we may have pending initBoxes that need to be processed
    // The incoming chunks will continue to build the init segment via handleBoxes
    // We just need to make sure the SourceBuffer is ready to receive them

    // Append first queued chunk if available (from normal flow)
    if (this.queue.length > 0) {
      const first = this.queue.shift()!;
      try {
        this.sourceBuffer.appendBuffer(first.chunk);
        console.log(`📦 Initial queued chunk appended: #${first.number}`);
      } catch (error) {
        console.error(`❌ Initial append failed for chunk #${first.number}:`, error);
        this.queue.unshift(first);
      }
    }
  };

  private logBufferDiagnostics() {
    console.group('🔍 Buffer Diagnostics');
    console.log('MediaSource readyState:', this.mediaSource?.readyState ?? 'null');

    // Check if sourceBuffer is still valid before accessing
    if (!this.sourceBuffer) {
      console.log('SourceBuffer: null (removed)');
      console.log('Video currentTime:', this.videoEl?.currentTime?.toFixed(3) ?? 'null');
      console.log('Video readyState:', this.videoEl?.readyState ?? 'null');
      console.log('Video error:', this.videoEl?.error ?? 'null');
      console.log('Queue length:', this.queue?.length ?? 0);
      console.groupEnd();
      return;
    }

    try {
      console.log('SourceBuffer updating:', this.sourceBuffer.updating);
      console.log('SourceBuffer mode:', this.sourceBuffer.mode);
      console.log('SourceBuffer timestampOffset:', this.sourceBuffer.timestampOffset);
      console.log('SourceBuffer appendWindowStart:', this.sourceBuffer.appendWindowStart);
      console.log('SourceBuffer appendWindowEnd:', this.sourceBuffer.appendWindowEnd);

      if (this.sourceBuffer.buffered.length > 0) {
        console.log('Buffered ranges:');
        for (let i = 0; i < this.sourceBuffer.buffered.length; i++) {
          const start = this.sourceBuffer.buffered.start(i);
          const end = this.sourceBuffer.buffered.end(i);
          console.log(
            `  Range ${i}: ${start.toFixed(3)}s - ${end.toFixed(3)}s (${(end - start).toFixed(3)}s)`
          );
        }
      } else {
        console.log('No buffered ranges');
      }
    } catch (e) {
      console.log('SourceBuffer: error accessing properties -', (e as Error).message);
    }

    console.log('Video currentTime:', this.videoEl?.currentTime?.toFixed(3) ?? 'null');
    console.log('Video readyState:', this.videoEl?.readyState ?? 'null');
    console.log('Video error:', this.videoEl?.error ?? 'null');
    console.log('Queue length:', this.queue?.length ?? 0);
    console.groupEnd();
  }

  // ==================== MP4 Box Buffering Methods ====================

  /**
   * Push incoming bytes into the rolling buffer
   */
  private pushBytes(chunk: Uint8Array) {
    const merged = new Uint8Array(this.byteBuffer.length + chunk.length);
    merged.set(this.byteBuffer);
    merged.set(chunk, this.byteBuffer.length);
    this.byteBuffer = merged;
  }

  /**
   * Extract complete MP4 boxes from the byte buffer.
   * Each box has: 4-byte size + 4-byte type + (size-8) bytes payload
   */
  private readBoxes(): { type: string; data: Uint8Array }[] {
    const boxes: { type: string; data: Uint8Array }[] = [];
    let offset = 0;

    while (this.byteBuffer.length - offset >= 8) {
      // Read box size (big-endian 32-bit)
      const size =
        (this.byteBuffer[offset] << 24) |
        (this.byteBuffer[offset + 1] << 16) |
        (this.byteBuffer[offset + 2] << 8) |
        this.byteBuffer[offset + 3];

      // Handle extended size (size == 1 means 64-bit size follows)
      let actualSize = size;
      if (size === 1 && this.byteBuffer.length - offset >= 16) {
        // 64-bit size in bytes 8-15 (we only use lower 32 bits for sanity)
        actualSize =
          (this.byteBuffer[offset + 12] << 24) |
          (this.byteBuffer[offset + 13] << 16) |
          (this.byteBuffer[offset + 14] << 8) |
          this.byteBuffer[offset + 15];
      } else if (size === 0) {
        // size == 0 means box extends to end of file, not valid for streaming
        console.warn('⚠️ Box with size 0 found, breaking');
        break;
      }

      // Not enough data for complete box yet
      if (this.byteBuffer.length - offset < actualSize) break;

      // Read box type
      const type = String.fromCharCode(
        this.byteBuffer[offset + 4],
        this.byteBuffer[offset + 5],
        this.byteBuffer[offset + 6],
        this.byteBuffer[offset + 7]
      );

      boxes.push({
        type,
        data: this.byteBuffer.slice(offset, offset + actualSize),
      });

      offset += actualSize;
    }

    // Keep remaining incomplete data in buffer
    this.byteBuffer = this.byteBuffer.slice(offset);
    return boxes;
  }

  /**
   * Concatenate multiple Uint8Arrays into one
   */
  private concatBuffers(arrays: Uint8Array[]): Uint8Array {
    const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const arr of arrays) {
      result.set(arr, offset);
      offset += arr.length;
    }
    return result;
  }

  /**
   * Safely append data to SourceBuffer with queue handling
   */
  private appendSafely(data: Uint8Array, description: string) {
    this.chunkCount++;

    if (!this.isReady || this.sourceBuffer.updating || this.isRemoving) {
      this.queue.push({ chunk: data, number: this.chunkCount });
      console.log(`📦 ${description} queued (#${this.chunkCount}), size: ${data.length}`);
      return;
    }

    try {
      this.sourceBuffer.appendBuffer(data);
      console.log(`✅ ${description} appended (#${this.chunkCount}), size: ${data.length} bytes`);
    } catch (error: unknown) {
      console.error(
        `❌ Append failed for ${description} (#${this.chunkCount}):`,
        (error as Error).message
      );
      this.logBufferDiagnostics();
    }
  }

  /**
   * Handle parsed boxes: collect init segment, pair moof+mdat for media
   */
  private handleBoxes(boxes: { type: string; data: Uint8Array }[]) {
    for (const box of boxes) {
      console.log(`📦 Box: ${box.type}, size: ${box.data.length}`);

      // FIRST: Check for new stream (ftyp or styp) - this MUST come before init segment logic
      // If we already have an init segment and see a new ftyp, it's a new stream
      if ((box.type === 'ftyp' || box.type === 'styp') && this.hasInitSegment) {
        console.log('🔄 New stream detected (ftyp/styp), resetting...');
        this.resetForNewStream(box.data);
        continue;
      }

      // Collecting init segment (ftyp, moov, etc.)
      if (!this.hasInitSegment) {
        this.initBoxes.push(box.data);

        if (box.type === 'moov') {
          // Init segment complete - append ftyp + moov together
          const initSegment = this.concatBuffers(this.initBoxes);
          console.log('🎬 Init segment complete, appending...');
          this.appendSafely(initSegment, 'Init segment');
          this.hasInitSegment = true;
          this.initBoxes = [];
        }
        continue;
      }

      // Media segment handling - pair moof + mdat
      if (box.type === 'moof') {
        this.pendingMoof = box.data;
        continue;
      }

      if (box.type === 'mdat' && this.pendingMoof) {
        // Append moof + mdat together as one media segment
        const mediaSegment = this.concatBuffers([this.pendingMoof, box.data]);
        this.appendSafely(mediaSegment, 'Media segment (moof+mdat)');
        this.pendingMoof = null;
        continue;
      }

      // Handle unexpected mdat without moof (shouldn't happen normally)
      if (box.type === 'mdat' && !this.pendingMoof) {
        console.warn('⚠️ mdat received without preceding moof, skipping');
        continue;
      }

      // Any other boxes are ignored (sidx, etc.)
      console.log(`ℹ️ Ignoring box type: ${box.type}`);
    }
  }

  /**
   * Reset state for a new incoming stream
   */
  private resetForNewStream(firstBox: Uint8Array) {
    // Pause before modifying buffer (remove/timestampOffset) to avoid inconsistent state
    if (!this.videoEl.paused) {
      this.videoEl.pause();
    }

    // If MediaSource is ended, recreate it
    if (this.mediaSource.readyState === 'ended') {
      console.log('⚠️ MediaSource is ended, recreating...');
      this.byteBuffer = new Uint8Array(0);
      this.pendingMoof = null;
      this.initBoxes = [firstBox];
      this.hasInitSegment = false;
      this.chunkCount = 0;
      this.playAttempted = false;
      this.pendingEndStream = false;
      this.recreateMediaSource();
      return;
    }

    // Normal reset for open MediaSource
    console.log('📍 Resetting for new stream (MediaSource is open)');
    this.queue = [];
    this.hasInitSegment = false;
    this.chunkCount = 0;
    this.playAttempted = false;
    this.pendingEndStream = false;
    this.pendingMoof = null;
    this.initBoxes = [firstBox];

    // Reset timestampOffset
    if (this.sourceBuffer && !this.sourceBuffer.updating) {
      this.sourceBuffer.timestampOffset = 0;
      console.log('📍 Reset timestampOffset to 0');
    }

    // Remove old buffered data
    if (this.sourceBuffer?.buffered?.length > 0 && !this.sourceBuffer.updating) {
      try {
        const start = this.sourceBuffer.buffered.start(0);
        const end = this.sourceBuffer.buffered.end(this.sourceBuffer.buffered.length - 1);
        console.log(`🧹 Clearing old buffer: ${start.toFixed(2)}s - ${end.toFixed(2)}s`);
        this.isRemoving = true;
        this.sourceBuffer.remove(start, end);
        this.videoEl.currentTime = 0;
      } catch (e) {
        console.warn('⚠️ Failed to clear buffer:', e);
        this.isRemoving = false;
      }
    } else {
      this.videoEl.currentTime = 0;
    }
  }

  // ==================== Public API ====================

  appendChunk(chunk: Uint8Array) {
    // Check if controller is destroyed
    if (this.isDestroyed) {
      console.warn('⚠️ Chunk ignored - controller is destroyed');
      return;
    }

    if (this.videoEl.error) {
      console.error('❌ Cannot append: video has error', this.videoEl.error.code);
      return;
    }

    if (chunk.length === 0) {
      console.warn('⚠️ Empty chunk received, skipping');
      return;
    }

    // Prevent queue overflow
    if (this.queue.length >= this.MAX_QUEUE_SIZE) {
      console.warn('⚠️ Queue full, dropping oldest chunk');
      this.queue.shift();
    }

    // Push bytes into buffer and extract complete boxes
    this.pushBytes(chunk);
    const boxes = this.readBoxes();

    if (boxes.length > 0) {
      this.handleBoxes(boxes);
    }
  }

  private recreateMediaSource() {
    console.log('🔄 Recreating MediaSource...');

    // Clean up old MediaSource
    try {
      if (this.sourceBuffer) {
        this.mediaSource.removeSourceBuffer(this.sourceBuffer);
      }
    } catch {}

    // Create new MediaSource
    this.mediaSource = new MediaSource();
    this.videoEl.src = URL.createObjectURL(this.mediaSource);
    this.sourceOpenHandled = false;
    this.isReady = false;
    this.hasInitSegment = false;
    this.playAttempted = false;

    this.mediaSource.addEventListener('sourceopen', this.onSourceOpen);
  }

  endStream() {
    console.log('⏹️ Stream end signal received');

    // Log video state for debugging
    const bufferedEnd =
      this.sourceBuffer?.buffered?.length > 0
        ? this.sourceBuffer.buffered.end(this.sourceBuffer.buffered.length - 1)
        : 0;
    const currentTime = this.videoEl.currentTime;
    const remainingBuffer = bufferedEnd - currentTime;

    console.log('📊 Video state:', {
      currentTime: currentTime.toFixed(2),
      bufferedEnd: bufferedEnd.toFixed(2),
      remainingBuffer: remainingBuffer.toFixed(2),
      duration: this.videoEl.duration,
      readyState: this.videoEl.readyState,
      paused: this.videoEl.paused,
      ended: this.videoEl.ended,
    });

    // End the stream so video duration becomes finite
    if (this.mediaSource.readyState === 'open') {
      if (!this.sourceBuffer?.updating) {
        try {
          this.mediaSource.endOfStream();
          console.log('✅ MediaSource ended, video should play to natural completion');

          // Check if video is already ended or very close to end
          const duration = this.videoEl.duration;
          const isAlreadyEnded =
            this.videoEl.ended ||
            (Number.isFinite(duration) && duration > 0 && duration - this.videoEl.currentTime < 0.1);

          if (isAlreadyEnded) {
            console.log('🎬 Video already at or near end');

            // If video.ended is true, the native ended event already fired
            if (this.videoEl.ended) {
              console.log('🎬 Video already ended, native ended should have handled state');
              return;
            }

            // Video is near end but ended event hasn't fired - wait for natural end
            console.log('🎬 Video near end, waiting for natural ended event');
            const onEnded = () => {
              console.log('🎬 Video ended event received');
              this.videoEl.removeEventListener('ended', onEnded);
              if (this.onStreamEndedCallback) this.onStreamEndedCallback();
            };
            this.videoEl.addEventListener('ended', onEnded);

            // Fallback timeout
            setTimeout(() => {
              if (!this.videoEl.ended) {
                this.videoEl.removeEventListener('ended', onEnded);
                console.log('⚠️ Fallback triggered, calling callback anyway');
                if (this.onStreamEndedCallback) this.forceVideoEnd();
              }
            }, 1000);
            return;
          }

          // Let the video play naturally to the end
          const onEnded = () => {
            console.log('🎬 Video ended event received (natural playback complete)');
            this.videoEl.removeEventListener('ended', onEnded);
            if (this.onStreamEndedCallback) this.onStreamEndedCallback();
          };

          this.videoEl.addEventListener('ended', onEnded);

          // Safety timeout: wait for remaining buffer time + small buffer
          const safetyTimeout = Math.max(remainingBuffer * 1000, 2000);
          console.log(`⏱️ Safety timeout set to ${(safetyTimeout / 1000).toFixed(1)}s`);

          setTimeout(() => {
            this.videoEl.removeEventListener('ended', onEnded);
            if (!this.videoEl.ended && this.onStreamEndedCallback) {
              console.log('⚠️ Video did not end naturally, triggering callback anyway');
              this.forceVideoEnd();
            }
          }, safetyTimeout);
        } catch (e) {
          console.warn('⚠️ Failed to end MediaSource:', e);
          // Still trigger callback on error
          if (this.onStreamEndedCallback) {
            this.forceVideoEnd();
          }
        }
      } else {
        console.log('⏳ SourceBuffer updating, will end MediaSource after updateend');
        this.pendingEndStream = true;
      }
    }
  }

  setOnStreamEnded(callback: () => void) {
    this.onStreamEndedCallback = callback;
  }

  destroy() {
    this.isDestroyed = true;
    this.queue = [];
    this.hasInitSegment = false;
    this.isReady = false;
    this.playAttempted = false;
    this.shouldEndWhenReady = false;

    // Clear MP4 box buffering state
    this.byteBuffer = new Uint8Array(0);
    this.pendingMoof = null;
    this.initBoxes = [];

    try {
      if (this.mediaSource.readyState === 'open') {
        this.mediaSource.endOfStream();
      }
    } catch {}
  }
}
